import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EngineeringIcon from "@mui/icons-material/Engineering";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import SyncIcon from "@mui/icons-material/Sync";
import CloseIcon from "@mui/icons-material/Close";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import BlingBillingRecipient from "../BlingBillingRecipient";

const EMPTY_ITEM = { descricao: "", qtd: 1, valorUnit: 0, valorTotal: 0, unidade: "UN", pricingTableId: null, tierLabel: null };
const UNIDADES = ["UN", "PC", "CX", "KG", "G", "L", "ML", "M", "M²", "M³", "H", "SV"];

async function fetchPricingTable(blingProductId) {
  try {
    const { data } = await api.get("/catalog/pricing-tables", { params: { blingProductId: String(blingProductId) } });
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch { return null; }
}

// Componente de busca e cadastro de cliente no Bling
const BlingClientSearch = ({ clientName, clientPhone, contactId, onChange }) => {
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const timer = useRef(null);

  const hasSelection = !!(clientName);

  const doSearch = (text) => {
    clearTimeout(timer.current);
    setNoResults(false);
    if (!text || text.trim().length < 2) { setOptions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const isPhone = /^[\d\s()+.-]{8,}$/.test(text.trim());
        const params = isPhone ? { telefone: text.trim() } : { nome: text.trim() };
        const { data } = await api.get("/bling/contacts/search", { params });
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        setNoResults(list.length === 0);
        setOpen(true);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const handleSelect = async (opt) => {
    setOpen(false); setSearchText(""); setOptions([]); setNoResults(false);
    try {
      const { data } = await api.post("/bling/contacts/resolve", {
        nome: opt.nome,
        phone: opt.celular || opt.telefone || "",
        blingContactId: opt.id,
        contactId: contactId || undefined,
      });
      onChange({
        clientName: opt.nome,
        clientPhone: opt.celular || opt.telefone || clientPhone || "",
        contactId: data.contactId || null,
      });
    } catch {
      onChange({ clientName: opt.nome, clientPhone: opt.celular || opt.telefone || clientPhone || "" });
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.warn("Informe o nome do cliente."); return; }
    setSaving(true);
    try {
      if (newPhone.trim()) {
        const { data: found } = await api.get("/bling/contacts/search", { params: { telefone: newPhone.trim() } }).catch(() => ({ data: [] }));
        if (Array.isArray(found) && found.length > 0) {
          const existing = found[0];
          toast.info(`Cliente encontrado no Bling: ${existing.nome}. Vinculando...`);
          const { data } = await api.post("/bling/contacts/resolve", {
            nome: existing.nome,
            phone: newPhone.trim(),
            blingContactId: existing.id,
            contactId: contactId || undefined,
          });
          onChange({ clientName: existing.nome, clientPhone: newPhone.trim(), contactId: data.contactId || null });
          setCreating(false); setNewName(""); setNewPhone("");
          return;
        }
      }
      const { data } = await api.post("/bling/contacts/resolve", {
        nome: newName.trim(),
        phone: newPhone.trim(),
        contactId: contactId || undefined,
      });
      toast.success("Cliente cadastrado no Bling");
      onChange({
        clientName: newName.trim(),
        clientPhone: newPhone.trim(),
        contactId: data.contactId || null,
      });
      setCreating(false); setNewName(""); setNewPhone("");
    } catch (err) {
      toastError(err);
    } finally { setSaving(false); }
  };

  const handleClear = () => {
    onChange({ clientName: "", clientPhone: "", contactId: null });
    setSearchText(""); setCreating(false);
  };

  if (hasSelection && !creating) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Chip
          label={`${clientName}${clientPhone ? ` · ${clientPhone}` : ""}`}
          onDelete={handleClear}
          style={{ backgroundColor: "#fff3e0", color: "#C05000", fontWeight: 600, maxWidth: 340 }}
        />
        <Button size="small" onClick={handleClear} style={{ color: "#888", fontSize: 11 }}>
          Alterar
        </Button>
      </div>
    );
  }

  if (creating) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Nome do cliente *" size="small" style={{ flex: 2, minWidth: 180 }}
            value={newName} onChange={e => setNewName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Telefone / WhatsApp" size="small" style={{ flex: 1, minWidth: 140 }}
            value={newPhone} onChange={e => setNewPhone(e.target.value)}
          />
          <Button
            variant="contained" size="small" onClick={handleCreate} disabled={saving}
            style={{ backgroundColor: "#F37021", color: "#fff", whiteSpace: "nowrap" }}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
          >
            Cadastrar
          </Button>
          <Button size="small" onClick={() => { setCreating(false); setNewName(""); setNewPhone(""); }}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <TextField
        label="Buscar cliente no Bling (nome ou telefone)" size="small" fullWidth
        value={searchText}
        onChange={e => { setSearchText(e.target.value); doSearch(e.target.value); }}
        onBlur={() => setTimeout(() => { setOpen(false); setNoResults(false); }, 200)}
        placeholder="Digite nome ou número..."
        InputProps={{
          endAdornment: loading
            ? <CircularProgress size={14} style={{ color: "#F37021" }} />
            : <SearchIcon fontSize="small" style={{ color: "#bbb" }} />,
        }}
      />
      {open && options.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", maxHeight: 280, overflowY: "auto",
        }}>
          {options.map(opt => (
            <div key={opt.id} onMouseDown={() => handleSelect(opt)}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff8f4"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{opt.nome}</div>
              {(opt.celular || opt.email) && (
                <div style={{ fontSize: 11, color: "#888", marginTop: 2, display: "flex", gap: 8 }}>
                  {opt.celular && <span>{opt.celular}</span>}
                  {opt.email && <span>{opt.email}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {noResults && !loading && searchText.trim().length >= 2 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", padding: "10px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}>
          <span style={{ fontSize: 12, color: "#999" }}>Nenhum cliente encontrado.</span>
          <Button
            size="small" variant="outlined"
            onClick={() => { setOpen(false); setCreating(true); setNewName(searchText); }}
            startIcon={<PersonAddIcon />}
            style={{ borderColor: "#F37021", color: "#F37021", fontSize: 11 }}
          >
            Cadastrar novo
          </Button>
        </div>
      )}
      {!open && !noResults && (
        <div style={{ marginTop: 4, textAlign: "right" }}>
          <Button size="small" onClick={() => setCreating(true)}
            startIcon={<PersonAddIcon />}
            style={{ color: "#F37021", fontSize: 11, padding: "0 4px" }}>
            Cadastrar novo cliente
          </Button>
        </div>
      )}
    </div>
  );
};

// Campo de descrição com busca Bling inline.
// - Digitando ≥2 letras: mostra dropdown com produtos
// - Ao selecionar: preenche só o nome no campo, libera edição livre
// - onTextChange(text): usuário editou o texto
// - onProductSelect({ valorUnit }): produto escolhido, preenche preço
// Highlights all occurrences of `terms` inside `text` (case/accent-insensitive)
function highlightTerms(text, terms) {
  if (!text || !terms?.length) return text;
  const normalize = s => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const norm = normalize(text);
  const parts = [];
  let last = 0;
  // collect all match positions
  const matches = [];
  for (const term of terms) {
    const t = normalize(term);
    if (!t) continue;
    let idx = 0;
    while ((idx = norm.indexOf(t, idx)) !== -1) {
      matches.push({ start: idx, end: idx + t.length });
      idx += t.length;
    }
  }
  if (!matches.length) return text;
  matches.sort((a, b) => a.start - b.start);
  for (const m of matches) {
    if (m.start < last) continue;
    if (m.start > last) parts.push(text.slice(last, m.start));
    parts.push(<mark key={m.start} style={{ background: "#FFE0C2", color: "#C05000", padding: 0, borderRadius: 2 }}>{text.slice(m.start, m.end)}</mark>);
    last = m.end;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const BlingItemSearch = ({ value, onTextChange, onProductSelect }) => {
  const [options, setOptions]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [noResults, setNoResults] = useState(false);
  const timer = useRef(null);

  const search = (text) => {
    clearTimeout(timer.current);
    // Start searching at 1 char (codes can be short); wait 400ms
    if (!text || text.trim().length < 1) {
      setOptions([]); setOpen(false); setNoResults(false); return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      setNoResults(false);
      try {
        const { data } = await api.get("/bling/products", { params: { search: text.trim(), limit: 15 } });
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        setLastQuery(text.trim());
        setOpen(true);
        setNoResults(list.length === 0);
      } catch { setOptions([]); setOpen(false); setNoResults(false); }
      finally { setLoading(false); }
    }, 400);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onTextChange(text);
    search(text);
  };

  const handleSelect = (opt) => {
    onTextChange(opt.nome);
    onProductSelect({ valorUnit: opt.preco || 0, id: opt.id, ncm: opt.ncm, codigo: opt.codigo, unidade: opt.unidade });
    setOptions([]);
    setOpen(false);
    setNoResults(false);
  };

  const searchWords = lastQuery.trim().split(/\s+/).filter(Boolean);

  return (
    <div style={{ position: "relative" }}>
      <TextField
        label="Descrição"
        size="small"
        fullWidth
        value={value}
        onChange={handleChange}
        onBlur={() => setTimeout(() => { setOpen(false); setNoResults(false); }, 180)}
        placeholder="Nome, palavras-chave ou código Bling..."
        InputProps={{
          endAdornment: loading
            ? <CircularProgress size={14} style={{ color: "#F37021" }} />
            : <SearchIcon fontSize="small" style={{ color: "#bbb" }} />,
        }}
      />
      {(open && options.length > 0) && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", maxHeight: 300, overflowY: "auto",
        }}>
          {options.map(opt => {
            const isVar = opt._isVariacao;
            const displayNome = isVar && opt._parentNome
              ? opt.nome.replace(opt._parentNome, "").trim().replace(/^[-–—\s]+/, "")
              : opt.nome;
            return (
              <div
                key={opt.id}
                onMouseDown={() => handleSelect(opt)}
                style={{
                  padding: isVar ? "5px 12px 5px 18px" : "7px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f5f5f5",
                  borderLeft: isVar ? "3px solid #F37021" : "3px solid transparent",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#fff8f4"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                {isVar && opt._parentNome && (
                  <div style={{ fontSize: 10, color: "#bbb", marginBottom: 1, lineHeight: 1.2 }}>
                    {opt._parentNome}
                  </div>
                )}
                <div style={{ fontWeight: 600, fontSize: 13, color: "#222", lineHeight: 1.3 }}>
                  {highlightTerms(displayNome || opt.nome, searchWords)}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {opt.codigo && (
                    <span style={{
                      fontFamily: "monospace",
                      background: isVar ? "#e8f4e8" : "#f0f0f0",
                      color: isVar ? "#1a5c1a" : "#555",
                      fontWeight: isVar ? 700 : 400,
                      borderRadius: 3, padding: "0 4px",
                    }}>
                      {highlightTerms(opt.codigo, searchWords)}
                    </span>
                  )}
                  {opt.preco > 0 && (
                    <span style={{ color: "#2a6030", fontWeight: 600 }}>
                      {Number(opt.preco).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </span>
                  )}
                  {opt.estoque != null && typeof opt.estoque !== "object" && (
                    <span style={{ color: opt.estoque > 0 ? "#aaa" : "#e53935" }}>
                      est: {opt.estoque} un.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {noResults && !loading && value.trim() && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", padding: "10px 14px",
        }}>
          <span style={{ fontSize: 12, color: "#999" }}>
            Nenhum produto encontrado. Tente palavras separadas (ex: "panfleto 4x4") ou o código exato.
          </span>
        </div>
      )}
    </div>
  );
};

// Situação do pedido no Bling (espelho D13) — só leitura. RH manda na situação.
const BLING_SITUACAO = {
  6: { label: "Em aberto", color: "#607d8b" },
  9: { label: "Atendido", color: "#2e7d32" },
  12: { label: "Cancelado", color: "#c62828" },
  15: { label: "Em andamento", color: "#1565c0" },
  18: { label: "Venda agenciada", color: "#6a1b9a" },
  21: { label: "Em digitação", color: "#8d6e63" },
  24: { label: "Verificado", color: "#00838f" },
  780710: { label: "Aguardando etiqueta", color: "#ef6c00" },
  780711: { label: "Em devolução", color: "#ad1457" },
};
const blingSituacao = (id) => id == null ? null : (BLING_SITUACAO[id] || { label: `Situação ${id}`, color: "#607d8b" });

const ServiceOrderDialog = ({ open, onClose, onSaved, order, initialTicketId }) => {
  const isEdit = Boolean(order?.id);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const [fiscalAlert, setFiscalAlert] = useState(null); // { fiscalReady, missing[], contactName }
  const [sendingReceipt, setSendingReceipt] = useState(null); // idx da parcela cujo recibo está sendo enviado

  const sendReceipt = async (idx) => {
    if (!order?.id) return;
    setSendingReceipt(idx);
    try {
      await api.post(`/service-orders/${order.id}/installment-receipt`, { installmentIndex: idx });
      toast.success("Recibo enviado no chat!");
    } catch (err) { toastError(err); }
    finally { setSendingReceipt(null); }
  };

  const [form, setForm] = useState({
    clientName: "", clientPhone: "", contactId: null,
    ticketId: initialTicketId || null,
    blingOrderId: "", blingOrderNumero: "",
    deadline: "", total: "", discount: "", paymentMethod: "",
    observations: "", productionNotes: "",
    fileApproved: false, prioridade: "normal", status: "aberta",
    responsibleId: user?.id || "", items: [{ ...EMPTY_ITEM }],
    attachments: [], installments: [],
    freteAtivo: false, freteEndereco: "", freteCep: "", freteObservacao: "", freteValor: "",
    billingEnabled: false, billingBlingContactId: null, billingName: "", billingDocument: "",
  });

  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [blingImporting, setBlingImporting] = useState(false);
  const [blingSyncing, setBlingSyncing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [contactBlingLinked, setContactBlingLinked] = useState(true);
  const [blingAddress, setBlingAddress] = useState(null); // { formatted, cep } do Bling

  // Ticket autocomplete
  const [ticketOptions, setTicketOptions] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketInputValue, setTicketInputValue] = useState("");
  const ticketTimer = useRef(null);

  // Ticket media (attachments from chat)
  const [ticketMedia, setTicketMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users", { params: { limit: 100 } }).then(({ data }) => {
      setUsers(Array.isArray(data.users) ? data.users : []);
    }).catch(() => {});
    api.get("/bling/payment-methods").then(({ data }) => {
      setPaymentMethods(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, [open]);

  // Load ticket if initialTicketId provided — always override fields
  useEffect(() => {
    if (!open || !initialTicketId) return;
    api.get(`/service-orders/ticket/${initialTicketId}/media`).then(({ data }) => {
      const c = data.ticket?.contact;
      setForm(f => ({
        ...f,
        ticketId: initialTicketId,
        clientName: c?.name || f.clientName,
        clientPhone: c?.number || f.clientPhone,
        contactId: c?.id || f.contactId,
      }));
      if (c) setTicketInputValue(`#${initialTicketId} — ${c.name || ""}`);
      setTicketMedia(data.media || []);
    }).catch(() => {});
  }, [open, initialTicketId]);

  // Load media when ticketId changes
  useEffect(() => {
    if (!form.ticketId) { setTicketMedia([]); return; }
    setMediaLoading(true);
    api.get(`/service-orders/ticket/${form.ticketId}/media`)
      .then(({ data }) => setTicketMedia(data.media || []))
      .catch(() => setTicketMedia([]))
      .finally(() => setMediaLoading(false));
  }, [form.ticketId]);

  // Verifica vínculo Bling e carrega endereço disponível para sugestão de frete
  useEffect(() => {
    setBlingAddress(null);
    if (!form.contactId) { setContactBlingLinked(true); return; }
    api.get(`/contacts/${form.contactId}`)
      .then(async ({ data }) => {
        setContactBlingLinked(!!data.blingContactId);
        if (!data.blingContactId) return;
        try {
          const { data: bc } = await api.get(`/bling/contact-detail/${data.blingContactId}`);
          const end = bc?.endereco;
          if (!end?.logradouro) return;
          const parts = [end.logradouro, end.numero, end.complemento, end.bairro, end.municipio, end.uf].filter(Boolean);
          setBlingAddress({ formatted: parts.join(", "), cep: end.cep || "" });
        } catch { /* sugestão é opcional */ }
      })
      .catch(() => setContactBlingLinked(true));
  }, [form.contactId]);

  // Populate form when editing
  // Checagem de dados fiscais (sob demanda, só ao abrir OS existente) — consulta o
  // Bling no backend (cacheado) e avisa se falta CPF/CNPJ/endereço p/ nota.
  useEffect(() => {
    if (!open || !order?.id) { setFiscalAlert(null); return; }
    let alive = true;
    setFiscalAlert(null);
    api.get(`/service-orders/${order.id}/fiscal-check`)
      .then(({ data }) => { if (alive) setFiscalAlert(data); })
      .catch(() => { /* silencioso — não bloqueia a OS */ });
    return () => { alive = false; };
  }, [open, order?.id]);

  useEffect(() => {
    if (!open) return;
    if (order) {
      setForm({
        clientName: order.clientName || "",
        clientPhone: order.clientPhone || "",
        contactId: order.contactId || null,
        ticketId: order.ticketId || null,
        blingOrderId: order.blingOrderId || "",
        blingOrderNumero: order.blingOrderNumero || "",
        deadline: order.deadline || "",
        total: order.total != null ? String(order.total) : "",
        discount: (() => { try { const p = JSON.parse(order.paymentMethod); return p.desconto ? String(p.desconto) : ""; } catch { return ""; } })(),
        paymentMethod: order.paymentMethod || "",
        observations: order.observations || "",
        productionNotes: order.productionNotes || "",
        fileApproved: order.fileApproved || false,
        prioridade: order.prioridade || "normal",
        status: order.status || "aberta",
        responsibleId: order.responsibleId || order.createdById || user?.id || "",
        items: order.items?.length ? order.items : [{ ...EMPTY_ITEM }],
        attachments: order.attachments || [],
        installments: Array.isArray(order.installments) ? order.installments : [],
        freteAtivo: order.freteAtivo || false,
        freteEndereco: order.freteEndereco || "",
        freteCep: order.freteCep || "",
        freteObservacao: order.freteObservacao || "",
        freteValor: order.freteValor != null ? String(order.freteValor) : "",
        billingEnabled: !!order.billingBlingContactId,
        billingBlingContactId: order.billingBlingContactId || null,
        billingName: order.billingName || "",
        billingDocument: order.billingDocument || "",
      });
    } else if (!initialTicketId) {
      setForm({
        clientName: "", clientPhone: "", contactId: null,
        ticketId: null, blingOrderId: "", blingOrderNumero: "",
        deadline: "", total: "", discount: "", paymentMethod: "",
        observations: "", productionNotes: "",
        fileApproved: false, prioridade: "normal", status: "aberta",
        responsibleId: user?.id || "", items: [{ ...EMPTY_ITEM }], attachments: [], installments: [],
        freteAtivo: false, freteEndereco: "", freteCep: "", freteObservacao: "", freteValor: "",
        billingEnabled: false, billingBlingContactId: null, billingName: "", billingDocument: "",
      });
    }
  }, [open, order]);

  // Ticket search
  const handleTicketSearch = (inputVal) => {
    setTicketInputValue(inputVal);
    if (!inputVal || inputVal.length < 2) { setTicketOptions([]); return; }
    clearTimeout(ticketTimer.current);
    ticketTimer.current = setTimeout(async () => {
      setTicketLoading(true);
      try {
        const { data } = await api.get("/tickets", { params: { searchParam: inputVal, pageNumber: 1, status: "open" } });
        const tickets = data.tickets || [];
        setTicketOptions(tickets.slice(0, 10));
      } catch { setTicketOptions([]); }
      finally { setTicketLoading(false); }
    }, 350);
  };

  const handleTicketSelect = (ticket) => {
    if (!ticket) {
      setForm(f => ({ ...f, ticketId: null }));
      return;
    }
    // Sempre sobrescreve nome e telefone com os dados do ticket selecionado
    setForm(f => ({
      ...f,
      ticketId: ticket.id,
      clientName: ticket.contact?.name || f.clientName,
      clientPhone: ticket.contact?.number || f.clientPhone,
      contactId: ticket.contact?.id || f.contactId,
    }));
    setTicketInputValue(`#${ticket.id} — ${ticket.contact?.name || ""}`);
    setTicketOptions([]);
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  // Usuário digitou texto livre na descrição
  const updateItemDescricao = (idx, text) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], descricao: text };
      return { ...f, items };
    });
  };

  const recalcTotal = (items, freteAtivo, freteValor) => {
    const itemsTotal = items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0);
    const fv = freteAtivo ? (parseFloat(freteValor) || 0) : 0;
    return (itemsTotal + fv).toFixed(2);
  };

  // Produto selecionado do catálogo Bling — preenche preço e recalcula
  const applyBlingProduct = async (idx, { valorUnit, id, ncm, codigo, unidade }) => {
    let pricingTable = null;
    if (id) pricingTable = await fetchPricingTable(String(id));

    setForm(f => {
      const items = [...f.items];
      const vu = pricingTable ? (valorUnit || 0) : (valorUnit || 0);
      items[idx] = {
        ...items[idx],
        valorUnit: vu,
        valorTotal: +((parseFloat(items[idx].qtd) || 1) * vu).toFixed(2),
        ncm: ncm || undefined,
        blingProductId: id || undefined,
        codigo: codigo || undefined,
        unidade: pricingTable ? pricingTable.unidade : (unidade || items[idx].unidade || "UN"),
        pricingTableId: pricingTable ? pricingTable.id : null,
        tierLabel: null,
      };
      const total = recalcTotal(items, f.freteAtivo, f.freteValor);
      return { ...f, items, total };
    });

    if (pricingTable) {
      const qtdAtual = parseFloat(form.items[idx]?.qtd) || 1;
      try {
        const { data } = await api.get(`/catalog/pricing-tables/${pricingTable.id}/calculate`, { params: { qty: qtdAtual } });
        setForm(f => {
          const items = [...f.items];
          if (!items[idx]) return f;
          items[idx] = { ...items[idx], valorUnit: data.unitPrice, valorTotal: +(qtdAtual * data.unitPrice).toFixed(2), tierLabel: data.tierLabel };
          return { ...f, items, total: recalcTotal(items, f.freteAtivo, f.freteValor) };
        });
      } catch { /* mantém preço base */ }
    }

    if (id && !ncm && !pricingTable) {
      api.get(`/bling/products/${id}`).then(({ data }) => {
        if (data.ncm) setForm(f => {
          const items = [...f.items];
          if (items[idx]) items[idx] = { ...items[idx], ncm: data.ncm };
          return { ...f, items };
        });
      }).catch(() => {});
    }
  };

  const updateItemField = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "qtd" || field === "valorUnit") {
        const qtd = field === "qtd" ? parseFloat(value) || 0 : parseFloat(items[idx].qtd) || 0;
        const vu = field === "valorUnit" ? parseFloat(value) || 0 : parseFloat(items[idx].valorUnit) || 0;
        items[idx].valorTotal = +(qtd * vu).toFixed(2);
      }
      const total = recalcTotal(items, f.freteAtivo, f.freteValor);
      return { ...f, items, total };
    });

    if (field === "qtd") {
      const item = form.items[idx];
      if (item?.pricingTableId) {
        const qty = parseFloat(value) || 0;
        if (qty > 0) {
          api.get(`/catalog/pricing-tables/${item.pricingTableId}/calculate`, { params: { qty } })
            .then(({ data }) => {
              setForm(f => {
                const items = [...f.items];
                if (!items[idx] || items[idx].pricingTableId !== item.pricingTableId) return f;
                items[idx] = { ...items[idx], valorUnit: data.unitPrice, valorTotal: +(qty * data.unitPrice).toFixed(2), tierLabel: data.tierLabel };
                return { ...f, items, total: recalcTotal(items, f.freteAtivo, f.freteValor) };
              });
            }).catch(() => {});
        }
      }
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerDefaultType, setMediaPickerDefaultType] = useState("outro");
  const fileInputRef = React.useRef(null);

  const toggleAttachment = (media, attachType) => {
    setForm(f => {
      const exists = f.attachments.some(a => a.url === media.url);
      return {
        ...f,
        attachments: exists
          ? f.attachments.filter(a => a.url !== media.url)
          : [...f.attachments, { url: media.url, name: media.name, type: attachType || "outro" }],
      };
    });
  };

  const updateAttachmentType = (url, newType) => {
    setForm(f => ({
      ...f,
      attachments: f.attachments.map(a => a.url === url ? { ...a, type: newType } : a),
    }));
  };

  const removeAttachment = (url) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter(a => a.url !== url) }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/messages/upload-temp", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url || data.mediaUrl;
      if (url) {
        setForm(f => ({
          ...f,
          attachments: [...f.attachments, { url, name: file.name, type: "outro" }],
        }));
      }
    } catch {
      toast.error("Erro ao anexar arquivo.");
    }
    e.target.value = "";
  };

  const handleFileApprovedChange = (checked) => {
    setForm(f => ({ ...f, fileApproved: checked }));
    if (checked) {
      const hasArte = form.attachments.some(a => a.type === "arte");
      if (!hasArte && form.ticketId) {
        setMediaPickerDefaultType("arte");
        setMediaPickerOpen(true);
      }
    }
  };

  const ATTACH_TYPES = [
    { value: "arte", label: "Arte" },
    { value: "crm", label: "CRM" },
    { value: "comprovante", label: "Comprovante" },
    { value: "outro", label: "Outro" },
  ];

  const handleBlingImport = async () => {
    if (!form.blingOrderNumero?.trim()) return;
    setBlingImporting(true);
    try {
      const { data } = await api.get(`/bling/order/${encodeURIComponent(form.blingOrderNumero.trim())}`);
      if (!data.items || data.items.length === 0) {
        toast.warn("Pedido Bling encontrado mas sem itens.");
        return;
      }
      setForm(f => ({
        ...f,
        blingOrderId: String(data.id),
        items: data.items.map(it => ({
          descricao: it.descricao,
          qtd: it.qtd,
          valorUnit: it.valorUnit,
          valorTotal: it.valorTotal,
        })),
        total: data.total != null ? String(data.total) : f.total,
        clientName: f.clientName || data.contato || f.clientName,
      }));
      toast.success(`Pedido #${data.numero} importado — ${data.items.length} item(s).`);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Pedido não encontrado no Bling.");
      } else {
        toastError(err);
      }
    } finally {
      setBlingImporting(false);
    }
  };

  const handleBlingSync = async () => {
    if (!order?.id) return;
    setBlingSyncing(true);
    try {
      // Salva a OS antes de sincronizar para que o backend leia dados atuais (frete, itens, etc.)
      await api.put(`/service-orders/${order.id}`, buildPayload());
      const { data } = await api.post(`/service-orders/${order.id}/bling-sync`);
      const isUpdate = !!form.blingOrderId;
      setForm(f => ({
        ...f,
        blingOrderId: data.blingOrderId || f.blingOrderId,
        blingOrderNumero: data.blingOrderNumero || f.blingOrderNumero,
      }));
      if (isUpdate) {
        toast.success(`Pedido Bling #${form.blingOrderNumero || form.blingOrderId} atualizado com sucesso!`);
      } else {
        toast.success(`Pedido Bling #${data.blingOrderNumero} criado com sucesso!`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || "";
      if (msg === "ERR_NO_ITEMS" || msg === "ERR_NO_PHONE") {
        toast.error("OS sem itens ou sem telefone — adicione itens antes de sincronizar.");
      } else if (typeof msg === "string" && msg.length > 0) {
        toast.error(msg);
      } else {
        toastError(err);
      }
    } finally {
      setBlingSyncing(false);
    }
  };

  // ── Installments helpers ──────────────────────────────────────────────────
  const totalNum   = parseFloat(form.total) || 0;
  const discountNum = parseFloat(form.discount) || 0;
  const netTotal   = Math.max(0, totalNum - discountNum);
  const totalPaid  = form.installments.reduce((s, p) => s + (p.paidAt ? Number(p.amount) : 0), 0);
  const totalDue   = netTotal - totalPaid;

  const addInstallment = () => {
    setForm(f => ({
      ...f,
      installments: [...f.installments, { label: `Parcela ${f.installments.length + 1}`, amount: "", dueDate: "", paidAt: null }],
    }));
  };

  const removeInstallment = (idx) => {
    setForm(f => ({ ...f, installments: f.installments.filter((_, i) => i !== idx) }));
  };

  const updateInstallment = (idx, field, value) => {
    setForm(f => {
      const installments = [...f.installments];
      installments[idx] = { ...installments[idx], [field]: value };
      return { ...f, installments };
    });
  };

  const togglePaid = (idx) => {
    setForm(f => {
      const installments = [...f.installments];
      installments[idx] = {
        ...installments[idx],
        paidAt: installments[idx].paidAt ? null : new Date().toISOString(),
      };
      return { ...f, installments };
    });
  };

  const applyPreset = (preset) => {
    const t = netTotal;
    const today = new Date().toISOString().split("T")[0];
    const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];
    const twoMonths = new Date(Date.now() + 60 * 86400000).toISOString().split("T")[0];
    if (preset === "5050") {
      const half = (t / 2).toFixed(2);
      setForm(f => ({ ...f, installments: [
        { label: "Entrada (50%)", amount: half, dueDate: today, paidAt: null },
        { label: "Saldo (50%)", amount: half, dueDate: nextMonth, paidAt: null },
      ]}));
    } else if (preset === "3x") {
      const third = (t / 3).toFixed(2);
      setForm(f => ({ ...f, installments: [
        { label: "1ª parcela", amount: third, dueDate: today, paidAt: null },
        { label: "2ª parcela", amount: third, dueDate: nextMonth, paidAt: null },
        { label: "3ª parcela", amount: third, dueDate: twoMonths, paidAt: null },
      ]}));
    } else if (preset === "3070") {
      setForm(f => ({ ...f, installments: [
        { label: "Entrada (30%)", amount: (t * 0.3).toFixed(2), dueDate: today, paidAt: null },
        { label: "Saldo (70%)", amount: (t * 0.7).toFixed(2), dueDate: nextMonth, paidAt: null },
      ]}));
    }
  };

  const buildPayload = () => {
    let resolvedPaymentMethod = form.paymentMethod;
    const disc = parseFloat(form.discount) || 0;
    if (disc > 0 && form.paymentMethod) {
      try {
        const pm = JSON.parse(form.paymentMethod);
        resolvedPaymentMethod = JSON.stringify({ ...pm, desconto: disc });
      } catch { /* free-text — keep as-is */ }
    }
    return {
      ...form,
      paymentMethod: resolvedPaymentMethod,
      total: form.total !== "" ? parseFloat(form.total) : null,
      // Responsável é obrigatório: se vazio, cai no usuário logado (nunca fica sem dono).
      responsibleId: form.responsibleId || user?.id || null,
      contactId: form.contactId || null,
      ticketId: form.ticketId || null,
      blingOrderId: form.blingOrderId || null,
      blingOrderNumero: form.blingOrderNumero || null,
      deadline: form.deadline || null,
      items: form.items.filter(it => it.descricao.trim()),
      attachments: form.attachments,
      installments: form.installments.filter(p => p.label || p.amount),
      freteAtivo: form.freteAtivo,
      freteEndereco: form.freteAtivo ? (form.freteEndereco || null) : null,
      freteCep: form.freteAtivo ? (form.freteCep || null) : null,
      freteObservacao: form.freteAtivo ? (form.freteObservacao || null) : null,
      freteValor: form.freteAtivo && form.freteValor ? parseFloat(form.freteValor) : null,
      // Faturamento em nome de terceiro — só persiste quando habilitado
      billingBlingContactId: form.billingEnabled ? (form.billingBlingContactId || null) : null,
      billingName: form.billingEnabled ? (form.billingName || null) : null,
      billingDocument: form.billingEnabled ? (form.billingDocument || null) : null,
    };
  };

  const handleSave = async () => {
    if (!form.responsibleId && !user?.id) {
      toast.warn("Selecione o responsável pela OS.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        const { data } = await api.put(`/service-orders/${order.id}`, payload);
        // Fase 1 sync: se a edição NÃO chegou ao Bling, avisa em vez do "salvo" silencioso.
        if (data?.blingSync && data.blingSync.ok === false) {
          toast.warning(`OS salva aqui, mas NÃO foi para o Bling: ${data.blingSync.detail || "divergência de sincronização"}`, { autoClose: 9000 });
        } else {
          toast.success("OS atualizada");
        }
        onSaved(data);
      } else {
        const { data } = await api.post("/service-orders", payload);
        toast.success("OS criada");
        onSaved(data);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const mediaTypeIcon = (type) => type === "image" ? "🖼" : type === "video" ? "🎥" : "📄";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <EngineeringIcon style={{ color: "#F37021" }} />
        {isEdit ? `Editar ${order.numero}` : "Nova Ordem de Serviço"}
      </DialogTitle>

      <DialogContent dividers>
        {order?.blingSituacaoId != null && (() => {
          const s = blingSituacao(order.blingSituacaoId);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: "0.82rem" }}>
              <span style={{ color: "#666" }}>Status no Bling:</span>
              <span style={{
                background: s.color, color: "#fff", borderRadius: 12,
                padding: "2px 10px", fontWeight: 600, fontSize: "0.78rem",
              }}>
                {s.label}
              </span>
              <span style={{ color: "#999", fontSize: "0.72rem" }}>(definido pelo RH no Bling)</span>
            </div>
          );
        })()}
        {order?.blingSyncPending && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#fdecea", color: "#b71c1c", border: "1px solid #f5c6cb",
            borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontWeight: 600, fontSize: "0.85rem",
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span>
              Divergente do Bling: {order.blingSyncMessage || "a última edição não foi sincronizada com o Bling."} Ajuste no Bling ou tente salvar de novo.
            </span>
          </div>
        )}
        {fiscalAlert && !fiscalAlert.fiscalReady && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#fff3e0", color: "#C05000", border: "1px solid #ffcc80",
            borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontWeight: 600, fontSize: "0.85rem",
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span>
              Sem dados para nota fiscal{fiscalAlert.contactName ? ` (${fiscalAlert.contactName})` : ""}: falta {fiscalAlert.missing?.join(", ")}.
            </span>
          </div>
        )}
        <Grid container spacing={2}>

          {/* === VÍNCULO COM TICKET === */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>VINCULAR TICKET (opcional)</Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              filterOptions={(x) => x}
              options={ticketOptions}
              loading={ticketLoading}
              inputValue={ticketInputValue}
              onInputChange={(_, v) => handleTicketSearch(v)}
              onChange={(_, ticket) => handleTicketSelect(ticket)}
              getOptionLabel={(t) => t ? `#${t.id} — ${t.contact?.name || ""} (${t.contact?.number || ""})` : ""}
              renderOption={(props, t) => (
                <li {...props} key={t.id}>
                  <div>
                    <span style={{ fontWeight: 700, fontFamily: "monospace", marginRight: 8 }}>#{t.id}</span>
                    <span>{t.contact?.name}</span>
                    <span style={{ color: "#999", fontSize: 12, marginLeft: 8 }}>{t.contact?.number}</span>
                    {t.lastMessage && <div style={{ fontSize: 11, color: "#aaa", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.lastMessage}</div>}
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar ticket por nome ou número..."
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon fontSize="small" style={{ color: "#aaa", marginRight: 4 }} />,
                    endAdornment: (
                      <>
                        {ticketLoading ? <CircularProgress size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {form.ticketId && (
              <Chip
                label={`Ticket #${form.ticketId} vinculado`}
                size="small"
                icon={<LinkIcon />}
                onDelete={() => setForm(f => ({ ...f, ticketId: null }))}
                style={{ marginTop: 6, backgroundColor: "#e3f2fd", color: "#1565c0" }}
              />
            )}
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* === CLIENTE === */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>CLIENTE</Typography>
            <BlingClientSearch
              clientName={form.clientName}
              clientPhone={form.clientPhone}
              contactId={form.contactId}
              onChange={({ clientName, clientPhone, contactId }) => {
                setForm(f => ({ ...f, clientName: clientName ?? f.clientName, clientPhone: clientPhone ?? f.clientPhone, contactId: contactId !== undefined ? contactId : f.contactId }));
                if (contactId !== undefined) setContactBlingLinked(true);
              }}
            />
            {form.clientName && !contactBlingLinked && (
              <div style={{ marginTop: 6, padding: "6px 10px", background: "#fff8e1", borderRadius: 6, border: "1px solid #ffe082", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 13, color: "#f57c00" }}>⚠️ Este cliente não está vinculado ao Bling. Abra o Cadastro do contato para completar os dados fiscais antes de gerar NF.</span>
              </div>
            )}
          </Grid>

          {/* === FATURAMENTO EM NOME DE TERCEIRO === */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.billingEnabled}
                  onChange={e => setForm(f => ({
                    ...f,
                    billingEnabled: e.target.checked,
                    ...(e.target.checked ? {} : { billingBlingContactId: null, billingName: "", billingDocument: "" }),
                  }))}
                  size="small"
                />
              }
              label={
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600 }}>
                  🧾 Faturar em nome de outra pessoa/empresa
                </span>
              }
            />
            {form.billingEnabled && (
              <div style={{ marginTop: 4 }}>
                <BlingBillingRecipient
                  value={{
                    billingBlingContactId: form.billingBlingContactId,
                    billingName: form.billingName,
                    billingDocument: form.billingDocument,
                  }}
                  onChange={(v) => setForm(f => ({ ...f, ...v }))}
                />
                <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#888" }}>
                  A NF e o pedido no Bling sairão no nome deste destinatário. O atendimento continua vinculado a {form.clientName || "este cliente"}.
                </span>
              </div>
            )}
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* === PEDIDO BLING (técnico — só admin; sync é automático p/ o operador) === */}
          {isAdmin && (
          <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>PEDIDO BLING (admin)</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="ID do pedido Bling" fullWidth size="small" value={form.blingOrderId} onChange={set("blingOrderId")} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
              <TextField
                label="Nº do pedido"
                fullWidth
                size="small"
                value={form.blingOrderNumero}
                onChange={set("blingOrderNumero")}
                placeholder="ex: 12345"
                onKeyDown={e => e.key === "Enter" && handleBlingImport()}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleBlingImport}
                disabled={blingImporting || !form.blingOrderNumero?.trim()}
                title="Importar itens do pedido Bling"
                style={{ minWidth: 36, height: 40, borderColor: "#F37021", color: "#F37021", padding: "0 8px", flexShrink: 0 }}
              >
                {blingImporting ? <CircularProgress size={14} style={{ color: "#F37021" }} /> : <DownloadIcon fontSize="small" />}
              </Button>
            </div>
          </Grid>
          </>
          )}
          <Grid item xs={12} sm={4}>
            <TextField label="Prazo de produção" fullWidth size="small" type="date" value={form.deadline} onChange={set("deadline")} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* === ITENS === */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              ITENS DO SERVIÇO — <span style={{ fontWeight: 400 }}>busca automática no catálogo Bling</span>
            </Typography>
          </Grid>
          {form.items.map((item, idx) => (
            <React.Fragment key={idx}>
              <Grid item xs={12} sm={5}>
                <BlingItemSearch
                  value={item.descricao}
                  onTextChange={(text) => updateItemDescricao(idx, text)}
                  onProductSelect={(product) => applyBlingProduct(idx, product)}
                />
              </Grid>
              <Grid item xs={3} sm={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>UN</InputLabel>
                  <Select
                    label="UN"
                    value={item.unidade || "UN"}
                    onChange={e => updateItemField(idx, "unidade", e.target.value)}
                  >
                    {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sm={2}>
                <TextField label="Qtd" fullWidth size="small" type="number"
                  value={item.qtd} onChange={e => updateItemField(idx, "qtd", e.target.value)}
                  inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Tooltip
                  title={item.tierLabel ? `Faixa: ${item.tierLabel}` : ""}
                  placement="top"
                  arrow
                  disableHoverListener={!item.tierLabel}
                >
                  <span>
                    <TextField label="Valor unit." fullWidth size="small" type="number"
                      value={item.valorUnit} onChange={e => updateItemField(idx, "valorUnit", e.target.value)}
                      inputProps={{ min: 0, step: "0.01" }} />
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={2} sm={1}>
                <TextField label="Subtotal" fullWidth size="small"
                  value={typeof item.valorTotal === "number" ? item.valorTotal.toFixed(2) : item.valorTotal}
                  disabled />
              </Grid>
              <Grid item xs={1}>
                <IconButton size="small" onClick={() => removeItem(idx)} disabled={form.items.length === 1}>
                  <DeleteIcon fontSize="small" style={{ color: form.items.length === 1 ? "#ccc" : "#d32f2f" }} />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}
          <Grid item xs={12}>
            <Button size="small" startIcon={<AddIcon />} onClick={addItem} style={{ color: "#F37021" }}>
              Adicionar item
            </Button>
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* === FRETE === */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.freteAtivo}
                  onChange={e => setForm(f => {
                    const fa = e.target.checked;
                    const total = recalcTotal(f.items, fa, f.freteValor);
                    return { ...f, freteAtivo: fa, total };
                  })}
                  size="small"
                />
              }
              label={
                <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:13, fontWeight:600 }}>
                  <LocalShippingIcon fontSize="small" style={{ color: form.freteAtivo ? "#F37021" : "#aaa" }} />
                  Incluir frete
                </span>
              }
            />
            {form.freteAtivo && (
              <Grid container spacing={1} style={{ marginTop:4 }}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Endereço de entrega" size="small" fullWidth
                    value={form.freteEndereco} onChange={set("freteEndereco")}
                    placeholder="Rua, número, bairro, cidade/UF" />
                  {blingAddress && (
                    <div style={{ marginTop: 3 }}>
                      <span
                        onClick={() => setForm(f => ({ ...f, freteEndereco: blingAddress.formatted, freteCep: blingAddress.cep || f.freteCep }))}
                        style={{ fontSize: 11, color: "#F37021", cursor: "pointer", textDecoration: "underline" }}
                        title="Clique para usar o endereço cadastrado no Bling"
                      >
                        Usar endereço do Bling: {blingAddress.formatted}
                      </span>
                    </div>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="CEP" size="small" fullWidth
                    value={form.freteCep} onChange={set("freteCep")}
                    placeholder="00000-000" inputProps={{ maxLength: 9 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Valor do frete (R$)" size="small" fullWidth type="number"
                    inputProps={{ min:0, step:"0.01" }}
                    value={form.freteValor}
                    onChange={e => setForm(f => {
                      const total = recalcTotal(f.items, f.freteAtivo, e.target.value);
                      return { ...f, freteValor: e.target.value, total };
                    })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Observação do frete (sai na etiqueta de envio)" size="small" fullWidth multiline minRows={1} maxRows={4}
                    value={form.freteObservacao} onChange={set("freteObservacao")}
                    placeholder="Ex.: ponto de referência, horário para entrega, deixar na portaria…" />
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* === PAGAMENTO === */}
          <Grid item xs={12} sm={4}>
            <TextField label="Total (R$)" fullWidth size="small" type="number"
              value={form.total} onChange={set("total")} inputProps={{ step: "0.01" }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Desconto (R$)" fullWidth size="small" type="number"
              value={form.discount} onChange={set("discount")}
              inputProps={{ step: "0.01", min: 0 }}
              helperText={discountNum > 0 ? `Líquido: ${netTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}` : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {paymentMethods.length > 0 ? (
              <Autocomplete
                options={paymentMethods}
                getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.descricao}
                value={(() => {
                  try {
                    const parsed = JSON.parse(form.paymentMethod);
                    return paymentMethods.find(m => m.id === parsed.id) || null;
                  } catch { return null; }
                })()}
                onChange={(_, opt) => {
                  setForm(f => ({
                    ...f,
                    paymentMethod: opt ? JSON.stringify({ id: opt.id, descricao: opt.descricao }) : "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Forma de pagamento" size="small" fullWidth />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              />
            ) : (
              <TextField label="Forma de pagamento" fullWidth size="small"
                value={form.paymentMethod} onChange={set("paymentMethod")}
                placeholder="ex: Pix, cartão, dinheiro" />
            )}
          </Grid>

          {/* === PARCELAS / CONTROLE DE RECEBIMENTO === */}
          <Grid item xs={12}><Divider /></Grid>
          <Grid item xs={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" color="textSecondary" style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: .7 }}>
                Controle de recebimento
              </Typography>
              {/* Presets */}
              {totalNum > 0 && (
                <>
                  <Chip size="small" label="50/50" onClick={() => applyPreset("5050")} clickable style={{ fontSize: 11 }} />
                  <Chip size="small" label="30/70 entrada" onClick={() => applyPreset("3070")} clickable style={{ fontSize: 11 }} />
                  <Chip size="small" label="3× iguais" onClick={() => applyPreset("3x")} clickable style={{ fontSize: 11 }} />
                </>
              )}
              <Button size="small" startIcon={<AddIcon />} onClick={addInstallment} style={{ color: "#F37021", marginLeft: "auto" }}>
                Parcela
              </Button>
            </div>

            {form.installments.length > 0 && (
              <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
                {/* Cabeçalho */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px 80px 36px", gap: 0, background: "#f5f5f5", padding: "4px 8px" }}>
                  {["Descrição", "Valor (R$)", "Vencimento", "Recebido?", ""].map(h => (
                    <span key={h} style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#666", letterSpacing: .5 }}>{h}</span>
                  ))}
                </div>
                {/* Linhas */}
                {form.installments.map((p, idx) => (
                  <div key={idx} style={{
                    display: "grid", gridTemplateColumns: "1fr 110px 130px 80px 36px",
                    gap: 0, padding: "4px 8px", borderTop: "1px solid #eee",
                    background: p.paidAt ? "#f0fdf4" : "#fff",
                    alignItems: "center",
                  }}>
                    <TextField
                      size="small" variant="standard"
                      value={p.label}
                      onChange={e => updateInstallment(idx, "label", e.target.value)}
                      inputProps={{ style: { fontSize: 13 } }}
                    />
                    <TextField
                      size="small" variant="standard" type="number"
                      value={p.amount}
                      onChange={e => updateInstallment(idx, "amount", e.target.value)}
                      inputProps={{ style: { fontSize: 13, textAlign: "right" }, step: "0.01", min: 0 }}
                    />
                    <TextField
                      size="small" variant="standard" type="date"
                      value={p.dueDate || ""}
                      onChange={e => updateInstallment(idx, "dueDate", e.target.value)}
                      inputProps={{ style: { fontSize: 13 } }}
                    />
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Tooltip title={p.paidAt ? `Recebido em ${new Date(p.paidAt).toLocaleDateString("pt-BR")}` : "Marcar como recebido"}>
                        <IconButton size="small" onClick={() => togglePaid(idx)}>
                          {p.paidAt
                            ? <CheckBoxIcon style={{ color: "#388e3c", fontSize: 20 }} />
                            : <CheckBoxOutlineBlankIcon style={{ color: "#aaa", fontSize: 20 }} />
                          }
                        </IconButton>
                      </Tooltip>
                    </div>
                    {p.paidAt && order?.id && (
                      <Tooltip title={form.ticketId ? "Enviar recibo desta parcela no chat" : "OS sem atendimento vinculado"}>
                        <span>
                          <IconButton size="small" disabled={!form.ticketId || sendingReceipt === idx} onClick={() => sendReceipt(idx)}>
                            <ReceiptLongIcon fontSize="small" style={{ color: form.ticketId ? "#F37021" : "#ccc" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => removeInstallment(idx)}>
                      <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />
                    </IconButton>
                  </div>
                ))}
                {/* Sumário */}
                <div style={{ display: "flex", gap: 16, padding: "6px 10px", borderTop: "2px solid #ddd", background: "#fafafa", justifyContent: "flex-end" }}>
                  {netTotal > 0 && (
                    <span style={{ fontSize: 12, color: "#555" }}>
                      {discountNum > 0
                        ? <>Líquido: <strong>{netTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong></>
                        : <>Total: <strong>{totalNum.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong></>
                      }
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: "#388e3c" }}>
                    Recebido: <strong>{totalPaid.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                  </span>
                  <span style={{ fontSize: 12, color: totalDue > 0 ? "#d32f2f" : "#388e3c" }}>
                    A receber: <strong>{Math.max(0, totalDue).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                  </span>
                </div>
              </div>
            )}
          </Grid>

          {/* === ANEXOS (arte, CRM, comprovante) === */}
          <Grid item xs={12}><Divider /></Grid>
          <Grid item xs={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <AttachFileIcon fontSize="small" style={{ color: "#888" }} />
              <Typography variant="subtitle2" color="textSecondary" style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: .7 }}>
                Anexos
              </Typography>
              {form.ticketId && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setMediaPickerDefaultType("outro"); setMediaPickerOpen(true); }}
                  style={{ fontSize: 11, borderColor: "#aaa", color: "#555", padding: "1px 8px", marginLeft: 4 }}
                >
                  {mediaLoading ? <CircularProgress size={10} /> : "Buscar na conversa"}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                style={{ fontSize: 11, borderColor: "#aaa", color: "#555", padding: "1px 8px" }}
              >
                Anexar arquivo
              </Button>
              <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileUpload} />
            </div>

            {form.attachments.length === 0 ? (
              <Typography variant="caption" color="textSecondary">Nenhum anexo adicionado.</Typography>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {form.attachments.map((att, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f9f9f9", border: "1px solid #e0e0e0", borderRadius: 6, padding: "4px 8px" }}>
                    <span style={{ fontSize: 18 }}>
                      {att.type === "arte" ? "🎨" : att.type === "crm" ? "📋" : att.type === "comprovante" ? "💳" : "📄"}
                    </span>
                    <span style={{ flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name}</span>
                    <Select
                      size="small"
                      variant="standard"
                      value={att.type || "outro"}
                      onChange={e => updateAttachmentType(att.url, e.target.value)}
                      style={{ fontSize: 11, minWidth: 100 }}
                      disableUnderline={false}
                    >
                      {ATTACH_TYPES.map(t => <MenuItem key={t.value} value={t.value} style={{ fontSize: 12 }}>{t.label}</MenuItem>)}
                    </Select>
                    <IconButton size="small" onClick={() => removeAttachment(att.url)}>
                      <DeleteIcon style={{ fontSize: 14, color: "#d32f2f" }} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Grid>

          {/* Picker de mídias da conversa */}
          {mediaPickerOpen && (
            <Grid item xs={12}>
              <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: 12, background: "#fafafa" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#555" }}>
                    Selecione mídias da conversa — tipo: {ATTACH_TYPES.find(t => t.value === mediaPickerDefaultType)?.label}
                  </Typography>
                  <IconButton size="small" onClick={() => setMediaPickerOpen(false)}>
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
                {ticketMedia.length === 0 && !mediaLoading && (
                  <Typography variant="caption" color="textSecondary">Nenhuma mídia encontrada nesta conversa.</Typography>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ticketMedia.map(media => {
                    const selected = form.attachments.some(a => a.url === media.url);
                    return (
                      <Tooltip key={media.id} title={media.name}>
                        <div
                          onClick={() => { toggleAttachment(media, mediaPickerDefaultType); }}
                          style={{
                            position: "relative", cursor: "pointer",
                            border: selected ? "2px solid #F37021" : "2px solid #ddd",
                            borderRadius: 6, overflow: "hidden", width: 64, height: 64,
                            background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          {media.mediaType === "image" ? (
                            <img src={media.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <span style={{ fontSize: 24 }}>{mediaTypeIcon(media.mediaType)}</span>
                          )}
                          {selected && (
                            <div style={{ position: "absolute", top: 2, right: 2, background: "#F37021", borderRadius: "50%", lineHeight: 0 }}>
                              <CheckBoxIcon style={{ fontSize: 16, color: "#fff" }} />
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
                <Button
                  size="small"
                  onClick={() => setMediaPickerOpen(false)}
                  style={{ marginTop: 8, color: "#F37021" }}
                >
                  Concluir seleção
                </Button>
              </div>
            </Grid>
          )}

          {/* === PRODUÇÃO === */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>PRODUÇÃO</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status} onChange={set("status")}>
                <MenuItem value="aberta">Aberta</MenuItem>
                <MenuItem value="em_producao">Em produção</MenuItem>
                <MenuItem value="pronta">Pronta</MenuItem>
                <MenuItem value="entregue">Entregue</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridade</InputLabel>
              <Select label="Prioridade" value={form.prioridade} onChange={set("prioridade")}>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="urgente">🔴 Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Emitida por"
              fullWidth
              size="small"
              value={order?.createdBy?.name || user?.name || "—"}
              InputProps={{ readOnly: true }}
              helperText="Identificado automaticamente"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" required error={!form.responsibleId}>
              <InputLabel id="resp-label">Responsável</InputLabel>
              <Select
                labelId="resp-label"
                label="Responsável"
                value={form.responsibleId || ""}
                onChange={set("responsibleId")}
              >
                {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </Select>
              <FormHelperText>{form.responsibleId ? "Toda OS tem um responsável" : "Obrigatório"}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.fileApproved} onChange={e => handleFileApprovedChange(e.target.checked)} style={{ color: "#F37021" }} />}
              label="Arquivo aprovado pelo cliente"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Observações de produção (interno)" fullWidth multiline rows={2} size="small"
              value={form.productionNotes} onChange={set("productionNotes")}
              placeholder="Ex: sangria 3mm, verificar Pantone..." />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Observações gerais" fullWidth multiline rows={2} size="small"
              value={form.observations} onChange={set("observations")} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        {isAdmin && isEdit && !form.blingOrderId && (
          <Button
            onClick={handleBlingSync}
            disabled={blingSyncing || saving}
            startIcon={blingSyncing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}
            style={{ color: "#F37021", borderColor: "#F37021" }}
            variant="outlined"
          >
            Sincronizar Bling
          </Button>
        )}
        {isAdmin && isEdit && form.blingOrderId && (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#777", display: "flex", alignItems: "center", gap: 4 }}>
              <SyncIcon style={{ fontSize: 14, color: "#388e3c" }} />
              Bling #{form.blingOrderNumero || form.blingOrderId}
            </span>
            <Button
              onClick={handleBlingSync}
              disabled={blingSyncing || saving}
              size="small"
              startIcon={blingSyncing ? <CircularProgress size={12} /> : <SyncIcon style={{ fontSize: 14 }} />}
              style={{ color: "#F37021", borderColor: "#F37021", fontSize: 11, padding: "2px 8px" }}
              variant="outlined"
            >
              Atualizar
            </Button>
          </span>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          style={{ backgroundColor: "#F37021", color: "#fff" }}
          startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
        >
          {isEdit ? "Salvar" : "Criar OS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceOrderDialog;
